// siteCopy.js — Phase 114 editable website copy.
// ---------------------------------------------------------------------------
// Every heading / subheading / label on the Pro coach website that used to be
// hard-coded now resolves through `getCopy()`. Coach overrides live in
// `landing_page.site_copy` as `{ [sectionKey]: { [field]: string } }`; an
// empty/missing value falls back to the auto-written default.
//
// COPY_SCHEMA drives BOTH the renderer (defaults) and the editor (which
// fields to show, labels, placeholders, max lengths). Defaults may be a
// string or a function of a `ctx` object ({ coach, lp, packages }) so
// auto-written copy can still mention the coach's name / service area.

export const COPY_SCHEMA = {
    hero: {
        title: 'Hero text',
        fields: [
            { key: 'eyebrow', label: 'Small badge above the headline', max: 60, default: (ctx) => (ctx.coach?.years_experience > 0 ? `Trusted for ${ctx.coach.years_experience}+ years` : 'Safety-first swim instruction') },
        ],
    },
    trust_strip: {
        title: 'Trust strip text',
        fields: [
            { key: 'verified_label', label: 'Verified badge text', max: 60, default: 'SplashPad Verified Coach' },
        ],
    },
    highlights: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: (ctx) => `Why families choose ${ctx.coach?.display_name || 'us'}` },
            { key: 'subheading', label: 'Subheading', multiline: true, max: 300, default: 'Everything about these lessons is designed to make swimmers safe, skilled and smiling.' },
        ],
    },
    programs_teaser: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'Your child’s swim journey starts here' },
            { key: 'subheading', label: 'Subheading', multiline: true, max: 300, default: 'Programs for every age and stage — pick the one that fits your family.' },
            { key: 'button_label', label: '“View all” button', max: 40, default: 'View all programs' },
        ],
    },
    packages: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'Lesson packages' },
            { key: 'subheading', label: 'Subheading', multiline: true, max: 300, default: 'Pick a package and we’ll guide you through the rest.' },
            { key: 'empty_text', label: 'Text when no packages are available', max: 200, default: 'No packages are currently available. Check back soon!' },
        ],
    },
    testimonials: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'What parents say' },
            { key: 'google_subtitle', label: 'Google badge subtitle', max: 120, default: 'Independent reviews from real families' },
            { key: 'google_button', label: 'Google badge button', max: 40, default: 'Read reviews' },
        ],
    },
    locations_teaser: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'Where we swim' },
            { key: 'button_label', label: '“All locations” button', max: 40, default: 'All locations' },
        ],
    },
    travel_check: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'Do we come to you?' },
            { key: 'subheading', label: 'Subheading', multiline: true, max: 300, default: 'Type your address and see the lesson times that fit our driving route — in-home lessons, no detours.' },
            { key: 'button_label', label: 'Button', max: 40, default: 'Check my address' },
        ],
    },
    locations_grid: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'Our locations' },
            { key: 'subheading', label: 'Subheading', multiline: true, max: 300, default: 'Tap a map button for directions in your map of choice.' },
        ],
    },
    faq: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'Frequently asked questions' },
        ],
    },
    cta_band: {
        title: 'Call-to-action text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'Ready to dive in?' },
            {
                key: 'subheading', label: 'Subheading', multiline: true, max: 300,
                default: (ctx) => (ctx.coach?.settings?.auto_approve_applications
                    ? 'Sign up in under a minute — instant approval, no waiting for review.'
                    : 'Apply now — your coach will review your application and get back to you within 24–48 hours.'),
            },
        ],
    },
    about_hero: {
        title: 'Page intro text',
        fields: [
            { key: 'title', label: 'Page title', max: 120, default: (ctx) => `Meet ${ctx.coach?.display_name || 'your coach'}` },
            { key: 'subtitle', label: 'Intro line', multiline: true, max: 300, default: (ctx) => (ctx.coach?.service_area ? `Serving ${ctx.coach.service_area}` : 'The story behind the lessons.') },
        ],
    },
    programs_hero: {
        title: 'Page intro text',
        fields: [
            { key: 'title', label: 'Page title', max: 120, default: 'Programs & pricing' },
            { key: 'subtitle', label: 'Intro line', multiline: true, max: 300, default: 'Find the perfect fit for your swimmer — every age, every stage.' },
        ],
    },
    locations_hero: {
        title: 'Page intro text',
        fields: [
            { key: 'title', label: 'Page title', max: 120, default: 'Where we swim' },
            { key: 'subtitle', label: 'Intro line', multiline: true, max: 300, default: 'Find the pool that’s closest to you.' },
        ],
    },
    contact_hero: {
        title: 'Page intro text',
        fields: [
            { key: 'title', label: 'Page title', max: 120, default: 'Get in touch' },
            { key: 'subtitle', label: 'Intro line', multiline: true, max: 300, default: 'Questions? We’d love to hear from you.' },
        ],
    },
    team_hero: {
        title: 'Page intro text',
        fields: [
            { key: 'title', label: 'Page title', max: 120, default: 'Meet the team' },
            { key: 'subtitle', label: 'Intro line', multiline: true, max: 300, default: (ctx) => `The coaches who make every lesson at ${ctx.coach?.display_name || 'our program'} possible.` },
        ],
    },
    story: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'My story' },
        ],
    },
    credentials: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'Credentials & experience' },
            { key: 'years_label', label: 'Years card text (use {n} for the number)', max: 80, default: '{n}+ years of coaching' },
        ],
    },
    gallery: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'In the water' },
            { key: 'subheading', label: 'Subheading', multiline: true, max: 300, default: 'A glimpse into our lessons.' },
        ],
    },
    videos: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'Watch us in action' },
        ],
    },
    contact_card: {
        title: 'Contact card text',
        fields: [
            { key: 'apply_title', label: 'Card 1 title', max: 80, default: 'Start lessons' },
            { key: 'apply_desc', label: 'Card 1 text', multiline: true, max: 200, default: 'Apply online — it takes about two minutes.' },
            { key: 'call_title', label: 'Card 2 title', max: 80, default: 'Call or text' },
            { key: 'call_desc', label: 'Card 2 text (shown when no phone is set)', multiline: true, max: 200, default: 'Reach out and we’ll get right back to you.' },
            { key: 'call_button', label: 'Card 2 button', max: 40, default: 'Call now' },
            { key: 'message_title', label: 'Card 3 title', max: 80, default: 'Send a message' },
            { key: 'message_desc', label: 'Card 3 text', multiline: true, max: 200, default: 'Questions about lessons, levels or scheduling.' },
        ],
    },
    team_grid: {
        title: 'Section text',
        fields: [
            { key: 'heading', label: 'Heading', max: 120, default: 'Our coaches' },
            { key: 'subheading', label: 'Subheading', multiline: true, max: 300, default: 'Certified, caring and here to help every swimmer thrive.' },
        ],
    },
    // Pseudo-sections (not on any page list) — edited from the Pages tab.
    nav: {
        title: 'Menu labels',
        fields: [
            { key: 'home', label: 'Home', max: 24, default: 'Home' },
            { key: 'about', label: 'About', max: 24, default: 'About' },
            { key: 'programs', label: 'Programs', max: 24, default: 'Programs' },
            { key: 'locations', label: 'Locations', max: 24, default: 'Locations' },
            { key: 'team', label: 'Team', max: 24, default: 'Meet the Team' },
            { key: 'contact', label: 'Contact', max: 24, default: 'Contact' },
        ],
    },
    footer: {
        title: 'Footer text',
        fields: [
            { key: 'tagline', label: 'Tagline under your name', multiline: true, max: 200, default: (ctx) => ctx.coach?.service_area || '' },
            { key: 'pages_label', label: '“Pages” column title', max: 30, default: 'Pages' },
            { key: 'contact_label', label: '“Contact” column title', max: 30, default: 'Contact' },
            { key: 'login_label', label: 'Parent login link', max: 40, default: 'Parent login' },
        ],
    },
};

/** Resolve a default (string or function of ctx). */
function resolveDefault(def, ctx) {
    if (typeof def === 'function') {
        try { return def(ctx || {}) || ''; } catch { return ''; }
    }
    return def || '';
}

/**
 * Get the coach-edited copy for `section.key`, falling back to the schema
 * default (or an explicit `fallback` when provided).
 *   getCopy(site, 'highlights', 'heading')
 */
export function getCopy(site, section, key, fallback) {
    const lp = site?.lp ?? site;
    const raw = lp?.site_copy?.[section]?.[key];
    if (typeof raw === 'string' && raw.trim()) return raw;
    if (fallback !== undefined) return fallback;
    const field = COPY_SCHEMA[section]?.fields?.find((f) => f.key === key);
    return field ? resolveDefault(field.default, site) : '';
}

/** Default text for an editor placeholder (same logic as the renderer). */
export function getCopyDefault(section, key, ctx) {
    const field = COPY_SCHEMA[section]?.fields?.find((f) => f.key === key);
    return field ? resolveDefault(field.default, ctx) : '';
}

/** Navbar / footer label for a page id, honoring `site_copy.nav`. */
export function getPageLabel(lp, pageId, fallbackLabel) {
    const raw = lp?.site_copy?.nav?.[pageId];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    return fallbackLabel || getCopyDefault('nav', pageId) || pageId;
}

/** Sections that expose editable copy in the editor (any key in COPY_SCHEMA). */
export function hasCopyFields(sectionId) {
    return !!COPY_SCHEMA[sectionId];
}
